import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import { getMemoryUsage } from 'src/utils/miscUtils';
import { ClientLogRequest, Feature, LogLevel } from 'src/WebApiController';

const MEMORY_CHECK_INTERVAL_MS = 30000; // 30 seconds

const MEMORY_THRESHOLD = 0.85; // Absolute max threshold (for local testing, set this to 0.01)
const MEMORY_THRESHOLD_MB = 1_024; // 1 GB
const LIGHTWEIGHT_CLEANUP_THROTTLE_MS = 3 * 60 * 1000; // 3 minutes between lightweight cleanups

function getTabId() {
  return sessionStorage.tabId
    ? sessionStorage.tabId
    : (sessionStorage.tabId = Math.random());
}

// Hook for memory cleanup
export const useMemoryCheckup = () => {
  const queryClient = useQueryClient();
  const { logEvent } = useClientLogContext();
  const lastLightweightCleanupRef = useRef<number>(0);

  const [memoryOverThresholdExceeded, setMemoryOverThresholdExceeded] =
    useState(undefined as undefined | [number, number]); // [currentUsageRatio, threshold]

  const hasShouldAutoCleanupBrowserCacheFeature = useUserHasFeature(
    Feature.ShouldAutoCleanupBrowserCache
  );

  const performMemoryCleanup = useCallback(() => {
    console.log('🧹 Performing emergency memory cleanup...');

    try {
      // Clear react-query cache
      queryClient.clear();
      queryClient.removeQueries();

      // 2. Clear browser caches (Service Worker, Cache API)
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (!cacheName.includes('critical')) {
              // Preserve critical caches
              caches.delete(cacheName);
            }
          });
        });
        console.log('✅ Browser caches cleared');
      }

      setMemoryOverThresholdExceeded(undefined);
      console.log('🎉 Memory cleanup completed');
    } catch (error) {
      console.error('❌ Error during memory cleanup:', error);
    }
  }, [queryClient]);

  const performLightWeightCleanup = useCallback(() => {
    console.log('🧹 Performing light-weight memory cleanup...');
    try {
      // Clear react-query cache
      queryClient.removeQueries({ stale: true, type: 'inactive' });
      console.log('🎉 Light-weight memory cleanup completed');
    } catch (error) {
      console.error('❌ Error during light-weight memory cleanup:', error);
    }
  }, [queryClient]);

  const alertOnHighMemoryUsage = useCallback(
    (memoryUsage: ReturnType<typeof getMemoryUsage>, type: string) => {
      const logRequest: ClientLogRequest = {
        logLevel: LogLevel.Warning,
        applicationSource: REACT_APP_NAME,
        errorId: getTabId(),
        error: 'HighMemoryUsage',
        pageUrl: window.location.href,
        componentStack: type,
        timestamp: new Date().toISOString(),
        additionalInfo: {
          ...memoryUsage,
          autoCleanEnabled: hasShouldAutoCleanupBrowserCacheFeature,
        },
      };
      logEvent(logRequest);
    },
    [hasShouldAutoCleanupBrowserCacheFeature, logEvent]
  );

  const onCheckMemoryUsage = useCallback(() => {
    const memoryUsage = getMemoryUsage();

    if (memoryUsage.usedJSHeapSize && memoryUsage.jsHeapSizeLimit) {
      const usageRatio =
        memoryUsage.usedJSHeapSize / memoryUsage.jsHeapSizeLimit;
      const usedMB = memoryUsage.usedJSHeapSize / 1024 / 1024;
      const limitMB = memoryUsage.jsHeapSizeLimit / 1024 / 1024;

      if (usedMB > MEMORY_THRESHOLD_MB) {
        const now = Date.now();
        const timeSinceLastCleanup = now - lastLightweightCleanupRef.current;

        // Throttle: skip cleanup if it was performed recently
        if (timeSinceLastCleanup > LIGHTWEIGHT_CLEANUP_THROTTLE_MS) {
          alertOnHighMemoryUsage(memoryUsage, 'MemoryUsageExceededLimit');
          lastLightweightCleanupRef.current = now;
          if (hasShouldAutoCleanupBrowserCacheFeature) {
            performLightWeightCleanup();
          }
        }
      }

      if (usageRatio > MEMORY_THRESHOLD) {
        console.error(
          `🚨 EMERGENCY: Critical memory usage: ${(usageRatio * 100).toFixed(1)}%`
        );
        console.error(
          `Used: ${usedMB.toFixed(1)}MB of ${limitMB.toFixed(1)}MB`
        );
        if (
          !memoryOverThresholdExceeded ||
          usageRatio !== memoryOverThresholdExceeded[0]
        ) {
          setMemoryOverThresholdExceeded([usageRatio, MEMORY_THRESHOLD]);
          alertOnHighMemoryUsage(memoryUsage, 'MemoryUsageExceededThreshold');
        }
      } else {
        if (memoryOverThresholdExceeded) {
          setMemoryOverThresholdExceeded(undefined);
        }
      }

      // Optional: Log current usage periodically for monitoring
      if (Math.random() < 0.1) {
        // 10% chance to log current usage
        console.debug(
          `📊 Memory usage: ${(usageRatio * 100).toFixed(1)}% (${usedMB.toFixed(1)}MB/${limitMB.toFixed(1)}MB)`
        );
      }
    }
  }, [
    alertOnHighMemoryUsage,
    hasShouldAutoCleanupBrowserCacheFeature,
    memoryOverThresholdExceeded,
    performLightWeightCleanup,
  ]);

  useEffect(() => {
    // Check memory every 30 seconds
    const interval = setInterval(onCheckMemoryUsage, MEMORY_CHECK_INTERVAL_MS);

    // Also check on certain browser events
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onCheckMemoryUsage();
      }
    };

    const handleFocus = () => {
      onCheckMemoryUsage();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [memoryOverThresholdExceeded, onCheckMemoryUsage]);

  return {
    performMemoryCleanup,
    memoryOverThresholdExceeded,
  };
};
